import { HttpClient, HttpClientModule } from '@angular/common/http';
import { inject, NgModule, SecurityContext } from '@angular/core';
import {
  ScreenTrackingService,
  UserTrackingService,
  getAnalytics,
  provideAnalytics,
} from '@angular/fire/analytics';
import { getApp, initializeApp, provideFirebaseApp } from '@angular/fire/app';
import {
  getAuth,
  browserLocalPersistence,
  initializeAuth,
  provideAuth,
} from '@angular/fire/auth';
import { getStorage, provideStorage } from '@angular/fire/storage';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouteReuseStrategy } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { Capacitor } from '@capacitor/core';
import {
  AuthenticationService,
  AuthorizationService,
  GroupsService,
  OrganisationsService,
  ProjectsService,
  SiteService,
} from '@ih/app/client/shared/services';
import {
  AuthenticationState,
  GroupAuthorisationState,
  NetworkState,
  OrganisationAuthorisationState,
  ProjectAuthorisationState,
  UiState,
  RedirectState,
} from '@ih/app/client/shared/states';
import { AppGoShellFeatureModule } from '@ih/app/go/shell/feature';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { NgxsActionsExecutingModule } from '@ngxs-labs/actions-executing';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsFormPluginModule } from '@ngxs/form-plugin';
import { NgxsLoggerPluginModule } from '@ngxs/logger-plugin';
import { NgxsRouterPluginModule } from '@ngxs/router-plugin';
import { NgxsStoragePluginModule } from '@ngxs/storage-plugin';
import { NgxsModule } from '@ngxs/store';
import { MarkdownModule } from 'ngx-markdown';
import { MomentModule } from 'ngx-moment';
import { environment } from '../environments/environment';
import { stateCacheKeys } from '../shared';
import { AppComponent } from './app.component';
import { createApollo, GraphQLModule } from './graphql.module';
import { provideApollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserAnimationsModule,
    BrowserModule,
    IonicModule.forRoot({
      mode: 'md',
    }),
    GraphQLModule,
    HttpClientModule,
    MarkdownModule.forRoot({
      loader: HttpClient,
      sanitize: SecurityContext.NONE,
    }),
    MomentModule.forRoot(),
    NgxsModule.forRoot(
      [
        AuthenticationState,
        ProjectAuthorisationState,
        GroupAuthorisationState,
        OrganisationAuthorisationState,
        NetworkState,
        UiState,
        RedirectState,
      ],
      {
        developmentMode: !environment.production,
      },
    ),
    NgxsActionsExecutingModule.forRoot(),
    NgxsFormPluginModule.forRoot(),
    NgxsLoggerPluginModule.forRoot({
      disabled: environment.production,
    }),
    NgxsReduxDevtoolsPluginModule.forRoot({
      disabled: environment.production,
    }),
    NgxsRouterPluginModule.forRoot(),
    NgxsStoragePluginModule.forRoot({ keys: stateCacheKeys }),
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.pwa,
      registrationStrategy: 'registerWhenStable:30000',
    }),
    AppGoShellFeatureModule,
  ],
  providers: [
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    // provideAuth(() => getAuth()),
    provideAuth(() => {
      if (Capacitor.isNativePlatform()) {
        return initializeAuth(getApp(), {
          persistence: browserLocalPersistence,
        });
      } else {
        return getAuth();
      }
    }),
    provideStorage(() => getStorage()),
    provideAnalytics(() => getAnalytics()),
    provideApollo(() => {
      const httpLink = inject(HttpLink);
      const authenticationService = inject(AuthenticationService);
      return createApollo(httpLink, authenticationService);
    }),
    AuthenticationService,
    ScreenTrackingService,
    UserTrackingService,
    ProjectsService,
    GroupsService,
    OrganisationsService,
    SiteService,
    AuthorizationService,
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
